// کارت وضعیت اعتبارنامه بیمه‌گر در میز کار (SCR-STAFF-500؛ AP-INT-09، US-INT-109).
//
// کارت **فقط وضعیت** را نشان می‌دهد: نه توکن، نه نام راز و نه شماره کامل (BR-INT-104)، و تنها برای خود ارائه‌دهنده دیده
// می‌شود. وضعیت `ExpiringSoon` از پیش‌فرض ۱۰ دقیقه پیش از انقضا می‌آید و دکمه «تمدید» صفحه اعتبارنامه را باز می‌کند.
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ProviderCredentialStatus } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { JalaliDate, StatusChip, type StatusTone } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { intKeys } from './common';

const TONE: Record<ProviderCredentialStatus, StatusTone> = {
  NotConfigured: 'inactive',
  Active: 'active',
  ExpiringSoon: 'pending',
  Expired: 'error',
  Invalid: 'error',
  NeedsBranch: 'pending',
};

/** وضعیت‌هایی که کارت را نشان می‌دهند (فعال و بی‌مشکل، کارت نمی‌خواهد). */
const SHOW: readonly ProviderCredentialStatus[] = ['ExpiringSoon', 'Expired', 'Invalid', 'NeedsBranch', 'NotConfigured'];

/** کارت‌های وضعیت اعتبارنامه بیمه‌گر برای میز کار پزشک (AC-INT-109.1). */
export function InsurerCredentialStatusCards() {
  const { t } = useTranslation('int');
  const { tenantApi, session } = useTenantSession();

  const status = useQuery({
    queryKey: intKeys.credentialStatus(session?.tenantId, session?.activeBranchId ?? undefined),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/integration/provider-credentials/status')),

    // AC-INT-109.2: وضعیت با رویداد `status_changed` روی SignalR به‌روز می‌شود؛ این بازخوانی دوره‌ای پشتوانه آن است.
    refetchInterval: 60_000,
  });

  const items = (status.data?.items ?? []).filter((item) => SHOW.includes(item.status));

  if (status.isPending || status.error || items.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1} data-testid="credential-status-card">
      {items.map((item) => (
        <Paper key={`${item.adapter}-${item.branchId ?? 'provider'}`} variant="outlined" sx={{ p: 1.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              {t(`int:insurer_credentials.insurer.${item.insurer}`, { defaultValue: item.insurer })}
            </Typography>
            <span data-testid={`credential-status-${item.insurer}`}>
              <StatusChip tone={TONE[item.status]} labelKey={`int:provider_credentials.status.${item.status}`} />
            </span>
            {item.expiresAt ? (
              <Typography variant="caption" color="text.secondary">
                <JalaliDate value={item.expiresAt} withTime />
              </Typography>
            ) : null}
            {item.cooperationMode === 'NonContracted' ? (
              <Typography variant="caption" color="warning.main">
                {t('int:provider_credentials.cooperation.NonContracted')}
              </Typography>
            ) : null}
            <Button
              size="small"
              href="/settings/integrations/insurer-credentials"
              data-testid={`credential-status-action-${item.insurer}`}
            >
              {item.status === 'NotConfigured'
                ? t('int:insurer_credentials.connect')
                : t('int:provider_credentials.renew')}
            </Button>
          </Stack>
          {item.status === 'Invalid' ? (
            <Alert severity="warning" sx={{ mt: 1 }}>
              {t('int:provider_credentials.mobile_required', { last4: '—' })}
            </Alert>
          ) : null}
        </Paper>
      ))}
    </Stack>
  );
}
